import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
    breadcrumWrapper?: InjectableCss;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumWrapper: {
        css: css`
            nav[data-test-selector^="pageBreadcrumbs"] {
                padding: 10px 70px 5px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 10px 20px 5px !important;
                                overflow: auto;
                                margin-right: 10px;
                            `,
                            css`
                                padding: 10px 30px 5px;
                            `,
                            null,
                        ],
                        "max",
                    )}
                a {
                    span {
                        white-space: nowrap;
                        color: #1a1a1a;
                        font-size: 13px;
                    }
                }
                span {
                    white-space: nowrap;
                }
                @media print {
                    display: none !important;
                }
            }
        `,
    },
};
const styles = pageBreadcrumbStyles;
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const PageBreadcrumbs = (props: Props) => {
    const links = props.links || generateLinksFrom(props.linksState, props.nodeId, props.homePageUrl);

    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, props.extendedStyles);
    return (
        <StyledWrapper {...styles.breadcrumWrapper}>
            <Breadcrumbs links={links} data-test-selector="pageBreadcrumbs" {...pageBreadcrumbStyles.breadcrumbs} />
        </StyledWrapper>
    );
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    const homePageLink = { children: translate("Home"), href: homePageUrl };
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);

    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));

    while (currentLink) {
        if (currentLink.type === "HomePage") {
            links.unshift(homePageLink);
        } else {
            const isVmiExcludePageType = !currentLink?.type
                ? false
                : vmiExcludePageTypes.some(search(currentLink.type));
            if (!isVmiPageType || !isVmiExcludePageType) {
                links.unshift({
                    children: currentLink.type === "HomePage" ? translate("Home_Breadcrumb") : currentLink.title,
                    href: currentLink.url,
                    type: currentLink.type,
                });
            }
        }
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
